import { isPlatformBrowser, NgClass } from '@angular/common';
import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  Inject,
  input,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  signal,
  ViewChild,
} from '@angular/core';
import { autoUpdate, computePosition, Placement } from '@floating-ui/dom';
import { GenericLogger } from '@scalefast/ecommerce-core';
import { Subscription } from 'rxjs';

const ARROW_WITH_PX = 16;
/**
 * The Popover component is a component that appears on a top layer attached to the component.
 * It uses native Popover to show hide the popover and custom positioning it with the floating-ui/dom library
 * It has additional features like arrowCenter, mockParentSize, and placement (bottom/top).
 * @TODO: use the anchor feature when available https://developer.mozilla.org/en-US/docs/Web/CSS/anchor
 */
@Component({
  selector: 'app-popover-wrapped',
  standalone: true,
  imports: [NgClass],
  templateUrl: './popover.component.html',
  styleUrl: './popover.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppPopoverComponent implements OnDestroy, OnInit {
  @ViewChild('popoverContent') popContent!: ElementRef<HTMLElement>;
  popOverTarget = input.required<HTMLElement>();
  /**
   * The placement of the popover relative to the popOverTarget element. Values bottom or top
   * Bottom by default.
   */
  placement = input<Placement>('bottom');
  /**
   * If true, the arrow of the popover will be on the center of the popOverTarget element.
   * Else the center of the popover will be on the center of the popOverTarget element.
   */
  arrowCenter = input<boolean>(false);

  /**
   * If true, the popover will be anchored to the parent size.
   */
  mockParentSize = input<boolean>(false);

  topPosition = signal('0px');
  leftPosition = signal('0px');
  popoverWidth = signal(284);
  private cleanup: () => void = () => {};
  private toggleDebounce = false;
  private toggleDebounceTimeout: NodeJS.Timeout | undefined;
  private openPopoverListener: Subscription | undefined;

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private logger: GenericLogger,
    private destroyRef: DestroyRef,
  ) {
    // We need the toggleDeferDate as clicking the button while popover is open will close it and open it again.
    afterNextRender(() => {
      this.popContent.nativeElement.addEventListener('beforetoggle', (event) => {
        this.toggleDebounce = true;
        this.toggleDebounceTimeout = setTimeout(() => {
          this.toggleDebounce = false;
        }, 100);
      });
    });
  }

  ngOnInit(): void {
    this.cleanup = () => {};
  }

  ngOnDestroy(): void {
    this.cleanup();
  }

  closePopover(): void {
    if (!this.isPlatformBrowser()) {
      return;
    }
    this.popContent.nativeElement.hidePopover();
  }

  openPopover(): void {
    if (!this.isPlatformBrowser()) {
      return;
    }

    // If the time between the last toggle is less than 100ms
    if (this.toggleDebounce) {
      this.toggleDebounce = false;
      clearTimeout(this.toggleDebounceTimeout);
      return;
    }

    this.anchorPopover();
    this.popContent.nativeElement.togglePopover();
  }

  private isPlatformBrowser(): boolean {
    if (isPlatformBrowser(this.platformId)) {
      return true;
    }
    this.logger.error(
      'Popover component is not supported in server side rendering. Use afterNextRender or equivalent to call this methods.',
    );
    return false;
  }

  /**
   * Anchors the popover to the popOverTarget element. Uses the library floating-ui/dom to compute the position of the popover.
   * The autoUpdate method must be called just before using it for and removed when the popover is closed performance reasons.
   * For more info look https://floating-ui.com/docs/autoUpdate
   */
  private anchorPopover(): void {
    const popOverTarget = this.popOverTarget();
    if (!popOverTarget) {
      return;
    }
    this.cleanup();
    this.cleanup = autoUpdate(popOverTarget, this.popContent.nativeElement, () => {
      computePosition(popOverTarget, this.popContent.nativeElement, { placement: this.placement() }).then(({ x, y }) => {
        let xPosition = x;
        // We have to set the width of the popover to the width of the popOverTarget element this way,
        // as popover element creates the popover on a top layer
        if (this.mockParentSize()) {
          // We add 8px to the width to compensate for the padding
          this.popoverWidth.set(popOverTarget.clientWidth + 8);
        }

        if (this.arrowCenter()) {
          xPosition = x + this.popoverWidth() / 2 - ARROW_WITH_PX;
        }

        this.topPosition.set(`${y}px`);
        this.leftPosition.set(`${xPosition}px`);
      });
    });
  }
}
